import React, { useMemo } from 'react';
import {
  Box, Stack, Typography, Autocomplete, TextField, FormControl, FormLabel,
  FormHelperText, FormControlLabel, Switch, Tabs, Tab, Chip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useReduxAuth';
import { useTeams } from '../../hooks/useTeams';
import { useGroups } from '../../hooks/useGroups';
import { useUsers } from '../../hooks/useUsers';
import { USER_ROLES } from '../../constants/permissions';
import { formFieldStyles } from './FormField.styles';

// DocumentBank atama alani: DynamicForm'un default FormField pattern'ine
// uyumlu. Value = Array<{type: 'COMPANY'|'GROUP'|'TEAM'|'USER', refId: number}>.
// Hook'lari kendi icinde cagirir, currentUser'i useAuth ile alir. TEAM_MANAGER
// scope kisitlamasi korunur: Sirket + Grup sekmeleri gizli, Ekip/Kisi sadece
// kendi ekibiyle sinirlidir.
const DocumentAssignmentsField = ({
  field, value, onChange, error, disabled = false,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const teamsHook = useTeams();
  const groupsHook = useGroups();
  const usersHook = useUsers();

  const teams = teamsHook?.teams || [];
  const groups = groupsHook?.groups || [];
  const users = usersHook?.users || [];
  const userCompanyId = currentUser?.companyId;
  const isTeamManagerOnly = currentUser?.role === USER_ROLES.TEAM_MANAGER;
  const myTeamId = currentUser?.team_id ?? currentUser?.teamId ?? null;

  // TEAM_MANAGER icin secim havuzunu kendi takim scope'una daralt.
  const scopedTeams = useMemo(() => {
    if (!isTeamManagerOnly) return teams;
    return teams.filter(tm => tm.id === myTeamId);
  }, [teams, isTeamManagerOnly, myTeamId]);

  const scopedUsers = useMemo(() => {
    if (!isTeamManagerOnly) return users;
    return users.filter(u => u.team_id === myTeamId);
  }, [users, isTeamManagerOnly, myTeamId]);

  // TEAM_MANAGER icin varsayilan tab = Ekip. Diger roller Sirket sekmesiyle
  // baslar. Kontrolsuz state kullaniyoruz cunku field yeniden mount olmamali.
  const [tab, setTab] = React.useState(isTeamManagerOnly ? 2 : 0);

  const list = Array.isArray(value) ? value : [];
  const emit = (next) => onChange && onChange(field.name, next);

  const companyOn = useMemo(
    () => list.some(a => a.type === 'COMPANY' && a.refId === userCompanyId),
    [list, userCompanyId],
  );
  const filterByType = (type) => list.filter(a => a.type === type);
  const setType = (type, items) => {
    const others = list.filter(a => a.type !== type);
    emit([...others, ...items.map(i => ({ type, refId: i.id }))]);
  };
  const setCompany = (on) => {
    const others = list.filter(a => a.type !== 'COMPANY');
    if (on && userCompanyId) emit([...others, { type: 'COMPANY', refId: userCompanyId }]);
    else emit(others);
  };

  const selectedGroups = useMemo(
    () => groups.filter(g => filterByType('GROUP').some(a => a.refId === g.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, list],
  );
  const selectedTeams = useMemo(
    () => scopedTeams.filter(tm => filterByType('TEAM').some(a => a.refId === tm.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedTeams, list],
  );
  const selectedUsers = useMemo(
    () => scopedUsers.filter(u => filterByType('USER').some(a => a.refId === u.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedUsers, list],
  );

  const userLabel = (u) =>
    [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.email || `#${u?.id}`;

  // Ozet chip listesi — kullanicinin secili atamalari bir bakista gormesi
  // icin (Sirket/Grup/Ekip/Kisi rozetleri).
  const summaryChips = useMemo(() => {
    const chips = [];
    if (companyOn) {
      chips.push({
        key: 'company',
        label: t('documentBank.assignments.company', 'Şirket'),
        color: 'primary',
        onDelete: disabled ? undefined : () => setCompany(false),
      });
    }
    selectedGroups.forEach(g => chips.push({
      key: `g-${g.id}`,
      label: `${t('documentBank.assignments.group', 'Grup')}: ${g.group_name || g.name || `#${g.id}`}`,
      color: 'info',
      onDelete: disabled ? undefined : () => setType('GROUP', selectedGroups.filter(x => x.id !== g.id)),
    }));
    selectedTeams.forEach(tm => chips.push({
      key: `t-${tm.id}`,
      label: `${t('documentBank.assignments.team', 'Ekip')}: ${tm.name || `#${tm.id}`}`,
      color: 'secondary',
      onDelete: disabled ? undefined : () => setType('TEAM', selectedTeams.filter(x => x.id !== tm.id)),
    }));
    selectedUsers.forEach(u => chips.push({
      key: `u-${u.id}`,
      label: `${t('documentBank.assignments.user', 'Kişi')}: ${userLabel(u)}`,
      color: 'default',
      onDelete: disabled ? undefined : () => setType('USER', selectedUsers.filter(x => x.id !== u.id)),
    }));
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOn, selectedGroups, selectedTeams, selectedUsers, disabled, t]);

  return (
    <FormControl fullWidth error={!!error} disabled={disabled} sx={formFieldStyles.field}>
      {field.label && (
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
          {t(field.label)}
          {field.required ? ' *' : ''}
        </FormLabel>
      )}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
        >
          {!isTeamManagerOnly && (
            <Tab value={0} icon={<BusinessIcon fontSize="small" />} iconPosition="start"
              label={t('documentBank.assignments.company', 'Şirket')} />
          )}
          {!isTeamManagerOnly && (
            <Tab value={1} icon={<GroupsIcon fontSize="small" />} iconPosition="start"
              label={t('documentBank.assignments.group', 'Grup')} />
          )}
          <Tab value={2} icon={<GroupIcon fontSize="small" />} iconPosition="start"
            label={t('documentBank.assignments.team', 'Ekip')} />
          <Tab value={3} icon={<PersonIcon fontSize="small" />} iconPosition="start"
            label={t('documentBank.assignments.user', 'Kişi')} />
        </Tabs>

        {tab === 0 && !isTeamManagerOnly && (
          <FormControlLabel
            control={
              <Switch
                checked={companyOn}
                onChange={(e) => setCompany(e.target.checked)}
                disabled={disabled}
              />
            }
            label={t('documentBank.assignments.thisCompany', 'Şirket genelinde herkes')}
          />
        )}
        {tab === 1 && !isTeamManagerOnly && (
          <Autocomplete
            multiple size="small" options={groups} disabled={disabled}
            getOptionLabel={(o) => o?.group_name || o?.name || `#${o?.id}`}
            value={selectedGroups}
            onChange={(_, v) => setType('GROUP', v)}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('documentBank.assignments.selectGroup', 'Grup seçin')} />
            )}
          />
        )}
        {tab === 2 && (
          <Autocomplete
            multiple size="small" options={scopedTeams} disabled={disabled}
            getOptionLabel={(o) => o?.name || `#${o?.id}`}
            value={selectedTeams}
            onChange={(_, v) => setType('TEAM', v)}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('documentBank.assignments.selectTeam', 'Ekip seçin')} />
            )}
          />
        )}
        {tab === 3 && (
          <Autocomplete
            multiple size="small" options={scopedUsers} disabled={disabled}
            getOptionLabel={userLabel}
            value={selectedUsers}
            onChange={(_, v) => setType('USER', v)}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('documentBank.assignments.selectUser', 'Kişi seçin')} />
            )}
          />
        )}

        {summaryChips.length > 0 && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {t('documentBank.assignments.selected', 'Seçilenler')}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {summaryChips.map(c => (
                <Chip key={c.key} size="small" label={c.label} color={c.color} variant="outlined" onDelete={c.onDelete} />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default DocumentAssignmentsField;
